/**
 * Grackle Brand Colors V2
 * Official brand color palette for Grackle CRM
 */

export const GRACKLE_COLORS = {
  /** Primary brand color — Prussian Blue #072744 */
  prussianBlue: '#072744',
  /** Accent / hover — Navy #183E6A */
  navy: '#183E6A',
  /** Light tint for secondary surfaces — Powder Blue #A0C1DB */
  powderBlue: '#A0C1DB',
  /** Destructive / error states — Dark Raspberry #941052 */
  darkRaspberry: '#941052',
  /** Warning / highlight — Pollen #FFC857 */
  pollen: '#FFC857',
  /** Light green accent / success fill — Pistachio #AFDBB1 (fill + dark text only) */
  pistachio: '#AFDBB1',
  /** Quiet panel / section background — Pale Blue #E7F0F6 */
  paleBlue: '#E7F0F6',
  /** Neutral surface / card background — Pale Grey #F5F5F5 */
  paleGrey: '#F5F5F5',

  /** Legacy aliases — kept temporarily so older imports compile.
   *  TODO: remove once all callsites migrate. */
  blue: '#072744',
  lightBlue: '#183E6A',
  turquoise: '#A0C1DB',
  turquoiseBg: '#183E6A',
  yellow: '#FFC857',
  magenta: '#941052',
  offWhite: '#F2F6FA',
} as const

/**
 * Chart color palette using Grackle V2 brand colors
 * Order chosen for visual contrast in adjacent series.
 */
export const CHART_COLORS = [
  GRACKLE_COLORS.prussianBlue,
  GRACKLE_COLORS.darkRaspberry,
  GRACKLE_COLORS.pollen,
  GRACKLE_COLORS.navy,
  GRACKLE_COLORS.powderBlue,
] as const

/**
 * Status color mapping using brand colors.
 * Note: success has no direct brand equivalent — uses a subdued green (#1B7A4A)
 * that complements the palette without competing with it.
 */
export const STATUS_COLORS = {
  /** @deprecated success is now a Pistachio fill — use SEMANTIC.success. */
  success: '#1B7A4A',
  warning: GRACKLE_COLORS.pollen,
  error: GRACKLE_COLORS.darkRaspberry,
  info: GRACKLE_COLORS.navy,
} as const

/**
 * Extended categorical palette (ratified in GRK-BRAND-1, docs/brand-decisions.md).
 * Grackle-harmonized hues for distinguishing categories (entity types, pipeline
 * stages, activity types) beyond the 5-accent core. Each family has:
 *   - `solid`: chart series / icon glyph / timeline dot / left-border.
 *             Verified >= 3:1 on white (WCAG non-text) EXCEPT `pollen`/`powder`,
 *             which are fill-only (add a Prussian stroke when used in charts).
 *   - `tint`:  badge/chip fill. Prussian text on it is >= 4.5:1 (AA).
 * Never use `solid` as coloured text on white unless it is prussian/navy/raspberry.
 */
export const CATEGORICAL = {
  prussian: { solid: '#072744', tint: '#E7F0F6' },
  navy: { solid: '#183E6A', tint: '#D8E2EE' },
  teal: { solid: '#0E7490', tint: '#CFE8EE' },
  evergreen: { solid: '#2E7D5B', tint: '#CDE9DB' },
  pollen: { solid: '#FFC857', tint: '#FFC857' },
  clay: { solid: '#B45309', tint: '#F5E3CC' },
  plum: { solid: '#6D2E6E', tint: '#ECD9EC' },
  steel: { solid: '#3F6B99', tint: '#DCE7F2' },
  raspberry: { solid: '#941052', tint: '#F3D6E4' },
  slate: { solid: '#52708A', tint: '#DCE4EC' },
  powder: { solid: '#A0C1DB', tint: '#A0C1DB' },
} as const

/**
 * Semantic status convention (GRK-BRAND-1). Each state is a fill + text pair,
 * so it renders as an accessible chip. Success has no brand green — it is a
 * Pistachio fill with Prussian text (pair with a check icon), never green text.
 */
export const SEMANTIC = {
  success: { fill: GRACKLE_COLORS.pistachio, text: GRACKLE_COLORS.prussianBlue },
  info: { fill: GRACKLE_COLORS.navy, text: '#FFFFFF' },
  warning: { fill: GRACKLE_COLORS.pollen, text: GRACKLE_COLORS.prussianBlue },
  error: { fill: GRACKLE_COLORS.darkRaspberry, text: '#FFFFFF' },
  neutral: { fill: GRACKLE_COLORS.paleGrey, text: GRACKLE_COLORS.navy },
} as const

/**
 * Activity-type colours (GRK-BRAND-1) — distinct hue per type, paired with an
 * icon. Use `.solid` for the timeline dot/icon and `.tint` for a chip fill.
 */
export const ACTIVITY_TYPE_COLORS = {
  Task: CATEGORICAL.navy,
  Call: CATEGORICAL.teal,
  Email: CATEGORICAL.plum,
  Event: CATEGORICAL.evergreen,
  Note: CATEGORICAL.steel,
} as const

/**
 * Extended chart-series order (GRK-BRAND-1) for charts needing more than the 5
 * CHART_COLORS. All entries are >= 3:1 on white; ordered for adjacent contrast.
 */
export const CHART_SERIES = [
  CATEGORICAL.prussian.solid,
  CATEGORICAL.raspberry.solid,
  CATEGORICAL.teal.solid,
  CATEGORICAL.pollen.solid,
  CATEGORICAL.evergreen.solid,
  CATEGORICAL.navy.solid,
  CATEGORICAL.clay.solid,
  CATEGORICAL.plum.solid,
  CATEGORICAL.steel.solid,
  CATEGORICAL.slate.solid,
] as const

/**
 * Neutral axis / gridline / tick colour for Recharts. The brand palette has no
 * grey, and chart axes need an unobtrusive neutral; this is the single canonical
 * value so no chart file carries a raw hex. ~5.7:1 on white (non-text UI).
 */
export const CHART_AXIS = '#666666'

/**
 * Converts hex color to HSL format for CSS variables
 * @param hex - Hex color string (e.g., '#253c82')
 * @returns HSL string without hsl() wrapper (e.g., '221 55% 31%')
 */
export function hexToHSL(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace('#', '')

  // Convert hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  h = Math.round(h * 360)
  s = Math.round(s * 100)
  const lPercent = Math.round(l * 100)

  return `${h} ${s}% ${lPercent}%`
}

/**
 * Get chart color by index (cycles through CHART_COLORS)
 */
export function getChartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length] ?? CHART_COLORS[0]
}

/**
 * Default Recharts margin tuned for Merriweather Sans + dense Card containers.
 * Use on charts without rotated XAxis labels.
 */
export const DEFAULT_CHART_MARGIN = { top: 8, right: 16, left: 12, bottom: 8 } as const

/**
 * Recharts margin for charts whose XAxis has rotated labels (e.g. angle={-45}).
 * Provides extra bottom room without growing the chart's container height.
 */
export const ROTATED_LABEL_CHART_MARGIN = { top: 8, right: 16, left: 12, bottom: 16 } as const

/**
 * Compact currency formatter for chart Y-axis ticks.
 * 1_100_000 → "$1.1M"   850_000 → "$850K"   42 → "$42"
 */
export function formatChartCurrency(value: number): string {
  if (!Number.isFinite(value) || value === 0) return '$0'
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`
  return `${sign}$${Math.round(abs)}`
}

/**
 * Compact count formatter for chart Y-axis ticks.
 * 1_200 → "1.2K"   850 → "850"
 */
export function formatChartCount(value: number): string {
  if (!Number.isFinite(value)) return '0'
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(Math.round(value))
}
